import { useState, useEffect, useCallback, useRef } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@shared/api/supabaseClient';
import { userApi } from '@shared/api/users';
import type { UserProfile, AuthState } from '@shared/types/auth';
import { logger } from '@shared/lib/logger';

// Cache for user profiles to prevent duplicate API calls
const userProfileCache = new Map<string, { profile: UserProfile; timestamp: number }>();
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes - increased for better persistence

// Function to fetch user profile from backend API with caching
const fetchUserProfile = async (userId: string): Promise<UserProfile> => {
  // Check cache first
  const cached = userProfileCache.get(userId);
  if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
    logger.debug('Using cached profile for user', { userId });
    return cached.profile;
  }

  // Check if we have a valid session with access token
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('No valid session found');
  }

  try {
    // Add timeout to prevent hanging - increased to 30 seconds for Supabase cold-start
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('API timeout after 30 seconds')), 30000)
    );

    logger.debug('Calling backend API for user profile', { userId });
    const profilePromise = userApi.getProfile();
    const profileResponse = await Promise.race([profilePromise, timeoutPromise]);
    logger.debug('Backend API response received', { userId, role: profileResponse.role });

    // Get user email and metadata from Supabase
    const { data: { user } } = await supabase.auth.getUser();
    const email = user?.email || 'user@example.com';
    const firstName = user?.user_metadata?.first_name || 'User';
    const lastName = user?.user_metadata?.last_name || 'Name';

    // Convert API response to UserProfile format
    const userProfile: UserProfile = {
      id: profileResponse.id,
      role: profileResponse.role,
      as_patient_id: profileResponse.as_patient_id,
      as_doctor_id: profileResponse.as_doctor_id,
      email,
      first_name: firstName,
      last_name: lastName,
      onboarding_complete: profileResponse.onboarding_complete || false,
      organization_roles: profileResponse.organization_roles ?? [],
      profile_image: profileResponse.profile_image,
    };

    // Cache the profile
    userProfileCache.set(userId, { profile: userProfile, timestamp: Date.now() });

    return userProfile;
  } catch (error) {
    logger.error('Backend API call failed', { userId, error });

    // Check if user is suspended
    const message = error instanceof Error ? error.message : String(error);
    const errorStatus = (error as any)?.status;
    const errorCode = (error as any)?.code;
    const errorName = (error as any)?.name;

    const isSuspended =
      message.includes('suspended') ||
      message.includes('SUSPENDED') ||
      message.includes('Your account has been suspended') ||
      errorStatus === 403 ||
      errorCode === 'FORBIDDEN' ||
      errorName === 'ApiClientError' && errorStatus === 403;

    // Debug logging for suspension detection
    console.log('Suspension check in useAuth:', {
      message,
      errorStatus,
      errorCode,
      errorName,
      isSuspended,
      errorType: error?.constructor?.name,
      fullError: error
    });

    if (isSuspended) {
      const suspensionMessage = 'Your account has been suspended. Please contact support for assistance.';
      logger.warn('User account suspended', { userId, message });
      // Redirect to error page with suspension message
      window.location.href = `/auth/error?message=${encodeURIComponent(suspensionMessage)}&code=SUSPENDED`;
      // Return a dummy profile to satisfy the return type, but the redirect will happen
      return {
        id: userId,
        role: 'patient' as const,
        as_patient_id: undefined,
        as_doctor_id: undefined,
        email: 'suspended@example.com',
        first_name: 'Suspended',
        last_name: 'User',
        onboarding_complete: false,
        organization_roles: []
      };
    }

    throw new Error(`Failed to fetch user profile: ${error instanceof Error ? error.message : String(error)}`);
  }
};

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>(() => {
    // Initialize from localStorage if available
    try {
      const saved = localStorage.getItem('jema_auth_state');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Only restore if it's recent (less than 24 hours old) - increased from 1 hour
        if (parsed.timestamp && (Date.now() - parsed.timestamp) < 24 * 60 * 60 * 1000) {
          logger.debug('Restoring auth state from localStorage', { userId: parsed.user?.id });
          return {
            user: parsed.user,
            isLoading: true, // Still validate session on mount
            isAuthenticated: !!parsed.user,
          };
        }
      }
    } catch (error) {
      logger.warn('Failed to restore auth state from localStorage', { error });
    }

    return {
      user: null,
      isLoading: true,
      isAuthenticated: false,
    };
  });

  const updateInProgress = useRef(false);
  const isInitialized = useRef(false);
  const lastSessionUserId = useRef<string | null>(null);

  // Save auth state to localStorage (stable function - no dependencies)
  const saveAuthState = useCallback((state: AuthState) => {
    try {
      localStorage.setItem('jema_auth_state', JSON.stringify({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        timestamp: Date.now(),
      }));
    } catch (error) {
      logger.warn('Failed to save auth state to localStorage', { error });
    }
  }, []);

  // Update auth state function (simplified and more robust)
  const updateAuthState = useCallback(async (session: Session | null, skipProfileCheck = false) => {
    // Prevent concurrent updates
    if (updateInProgress.current) {
      logger.debug('Auth update already in progress, skipping');
      return;
    }

    updateInProgress.current = true;

    try {
      if (session?.user && session.access_token) {
        const userId = session.user.id;
        logger.debug('Processing auth session', { userId, skipProfileCheck });

        // If we have a cached profile and we're not doing initial load, use it
        if (skipProfileCheck && lastSessionUserId.current === userId) {
          const cached = userProfileCache.get(userId);
          if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
            logger.debug('Using cached profile for session update', { userId });
            const newState = {
              user: cached.profile,
              isLoading: false,
              isAuthenticated: true,
            };
            setAuthState(newState);
            saveAuthState(newState);
            updateInProgress.current = false;
            return;
          }
        }

        lastSessionUserId.current = userId;
        logger.debug('Fetching user profile', { userId });

        try {
          const userProfile = await fetchUserProfile(userId);
          logger.info('User profile fetched successfully', { userId, role: userProfile.role });

          const newState = {
            user: userProfile,
            isLoading: false,
            isAuthenticated: true,
          };

          setAuthState(newState);
          saveAuthState(newState);
        } catch (error) {
          logger.error('Profile fetch failed', { userId, error });

          // Check if user is suspended
          const message = error instanceof Error ? error.message : String(error);
          const errorStatus = (error as any)?.status;
          const errorCode = (error as any)?.code;
          const errorName = (error as any)?.name;

          const isSuspended =
            message.includes('suspended') ||
            message.includes('SUSPENDED') ||
            message.includes('Your account has been suspended') ||
            errorStatus === 403 ||
            errorCode === 'FORBIDDEN' ||
            errorName === 'ApiClientError' && errorStatus === 403;

          // Debug logging for suspension detection
          console.log('Suspension check in useAuth main handler:', {
            message,
            errorStatus,
            errorCode,
            errorName,
            isSuspended,
            errorType: error?.constructor?.name,
            fullError: error
          });

          if (isSuspended) {
            const suspensionMessage = 'Your account has been suspended. Please contact support for assistance.';
            logger.warn('User account suspended', { userId, message });
            // Clear auth state and redirect to suspension page
            const newState = {
              user: null,
              isLoading: false,
              isAuthenticated: false,
            };
            setAuthState(newState);
            saveAuthState(newState);
            window.location.href = `/auth/error?message=${encodeURIComponent(suspensionMessage)}&code=SUSPENDED`;
            return;
          }

          // If we have a cached profile, use it as fallback
          const cached = userProfileCache.get(userId);
          if (cached) {
            logger.warn('Using cached profile as fallback', { userId });
            const newState = {
              user: cached.profile,
              isLoading: false,
              isAuthenticated: true,
            };
            setAuthState(newState);
            saveAuthState(newState);
          } else {
            // No cached profile, clear session
            logger.warn('No cached profile available, clearing session');
            const newState = {
              user: null,
              isLoading: false,
              isAuthenticated: false,
            };
            setAuthState(newState);
            saveAuthState(newState);
            await supabase.auth.signOut();
          }
        }
      } else {
        logger.debug('No valid session, setting unauthenticated state');
        lastSessionUserId.current = null;

        const newState = {
          user: null,
          isLoading: false,
          isAuthenticated: false,
        };

        setAuthState(newState);
        saveAuthState(newState);
      }
    } finally {
      updateInProgress.current = false;
    }
  }, [saveAuthState]);

  // Main initialization effect - runs only once
  useEffect(() => {
    if (isInitialized.current) return;

    let mounted = true;
    isInitialized.current = true;

    const getInitialSession = async () => {
      try {
        logger.debug('Checking initial session');

        // Get session with longer timeout for better reliability
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Initial session check timeout')), 10000);
        });

        const { data: { session } } = await Promise.race([sessionPromise, timeoutPromise]);

        if (mounted) {
          logger.info('Initial session check complete', { hasSession: !!session, userId: session?.user?.id });
          await updateAuthState(session, false); // Initial load - fetch profile
        }
      } catch (error) {
        if (mounted) {
          logger.warn('Initial session check failed', { error });

          // If we have stored state, use it and stop loading
          const hasStoredState = localStorage.getItem('jema_auth_state');
          if (hasStoredState) {
            logger.debug('Using stored auth state as fallback');
            setAuthState(prev => ({ ...prev, isLoading: false }));
          } else {
            const newState = {
              user: null,
              isLoading: false,
              isAuthenticated: false,
            };
            setAuthState(newState);
            saveAuthState(newState);
          }
        }
      }
    };

    // Start session check after small delay to avoid React StrictMode issues
    const initTimeout = setTimeout(() => {
      if (mounted) {
        getInitialSession();
      }
    }, 100);

    return () => {
      mounted = false;
      if (initTimeout) {
        clearTimeout(initTimeout);
      }
    };
  }, [updateAuthState, saveAuthState]);

  // Separate effect for auth state changes
  useEffect(() => {
    let mounted = true;
    let debounceTimeout: NodeJS.Timeout;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted || !isInitialized.current) return;

      logger.debug('Auth state changed', { event, hasSession: !!session, userId: session?.user?.id });

      // Clear previous debounce
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }

      // Debounce auth state changes
      debounceTimeout = setTimeout(async () => {
        if (mounted && isInitialized.current) {
          // Skip profile fetch for subsequent calls - use cached profile
          await updateAuthState(session, true);
        }
      }, 150);
    });

    return () => {
      mounted = false;
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      subscription.unsubscribe();
    };
  }, [updateAuthState]);

  // Failsafe effect - separate to avoid conflicts
  useEffect(() => {
    const failsafeTimeout = setTimeout(() => {
      setAuthState(current => {
        if (current.isLoading) {
          logger.warn('Auth failsafe triggered: stopping loading state');
          const newState = { ...current, isLoading: false };
          saveAuthState(newState);
          return newState;
        }
        return current;
      });
    }, 15000); // Increased to 15s to allow more time for session validation

    return () => clearTimeout(failsafeTimeout);
  }, [saveAuthState]);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw error;
    }

    return data;
  }, []);

  const signInWithMagicLink = useCallback(async (email: string) => {
    // Use app URL if configured, otherwise fall back to current origin
    // This allows magic links to work in webview apps by using a consistent domain
    const appUrl = import.meta.env.VITE_APP_URL;
    const redirectUrl = appUrl 
      ? `${appUrl}/auth/callback`
      : `${window.location.origin}/auth/callback`;

    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });

    if (error) {
      throw error;
    }

    return data;
  }, []);

  const signOut = useCallback(async () => {
    // Clear all caches and stored data
    userProfileCache.clear();
    localStorage.removeItem('jema_auth_state');
    lastSessionUserId.current = null;

    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }
  }, []);

  const refreshSession = useCallback(async () => {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      throw error;
    }
    return data;
  }, []);

  // Add debug utilities in development
  useEffect(() => {
    if (import.meta.env.DEV) {
      (window as any).jemaAuth = {
        currentState: authState,
        forceRefresh: () => {
          logger.info('Forcing auth refresh from debug utility');
          isInitialized.current = false;
          lastSessionUserId.current = null;
          setAuthState(prev => ({ ...prev, isLoading: true }));

          supabase.auth.getSession().then(({ data: { session } }) => {
            updateAuthState(session, false);
          });
        },
        clearCache: () => {
          logger.info('Clearing auth cache from debug utility');
          userProfileCache.clear();
          localStorage.removeItem('jema_auth_state');
          lastSessionUserId.current = null;
        },
        getSessionInfo: async () => {
          const { data: { session } } = await supabase.auth.getSession();
          return {
            hasSession: !!session,
            userId: session?.user?.id,
            email: session?.user?.email,
            expiresAt: session?.expires_at,
            accessToken: session?.access_token ? 'present' : 'missing'
          };
        }
      };
    }
  }, [authState, updateAuthState]);

  return {
    ...authState,
    signInWithPassword,
    signInWithMagicLink,
    signOut,
    refreshSession,
  };
};
